import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Calendar,
  MapPin,
  Package,
  Hotel,
  Activity,
  AlertCircle
} from 'lucide-react'

interface AnalyticsProps {
  tenantId: string
}

interface AnalyticsData {
  revenue: {
    total: number
    monthly: number
    growth: number
  }
  bookings: {
    total: number
    confirmed: number
    pending: number
    cancelled: number
  }
  topDestinations: Array<{
    location: string
    bookings: number
    revenue: number
  }>
  bookingTrends: Array<{
    month: string
    bookings: number
    revenue: number
  }>
  productPerformance: {
    hotels: { count: number, bookings: number, revenue: number }
    activities: { count: number, bookings: number, revenue: number }
    packages: { count: number, bookings: number, revenue: number }
  }
}

const Analytics: React.FC<AnalyticsProps> = ({ tenantId }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d')
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    revenue: { total: 0, monthly: 0, growth: 0 },
    bookings: { total: 0, confirmed: 0, pending: 0, cancelled: 0 },
    topDestinations: [],
    bookingTrends: [],
    productPerformance: {
      hotels: { count: 0, bookings: 0, revenue: 0 },
      activities: { count: 0, bookings: 0, revenue: 0 },
      packages: { count: 0, bookings: 0, revenue: 0 }
    }
  })

  useEffect(() => {
    fetchAnalytics()
  }, [tenantId, timeRange])

  const fetchAnalytics = async () => {
    setError(null)
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/analytics?tenant_id=${tenantId}&timeRange=${timeRange}`)
      const data = await response.json()
      if (data.success) {
        setAnalytics(data.analytics)
      } else {
        setError(data.message || 'Failed to fetch analytics data')
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error)
      setError('Unable to connect to analytics service. Please check your connection.')
      // Keep empty state - show error message to user instead of mock data
      setAnalytics({
        revenue: { total: 0, monthly: 0, growth: 0 },
        bookings: { total: 0, confirmed: 0, pending: 0, cancelled: 0 },
        topDestinations: [],
        bookingTrends: [],
        productPerformance: {
          hotels: { count: 0, bookings: 0, revenue: 0 },
          activities: { count: 0, bookings: 0, revenue: 0 },
          packages: { count: 0, bookings: 0, revenue: 0 }
        }
      })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-red-800">Analytics Unavailable</h3>
            <p className="text-red-700 mt-1">{error}</p>
            <button 
              onClick={fetchAnalytics}
              className="mt-3 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Track your business performance and trends</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>
      </div>

      {/* Revenue Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">Total Revenue</p>
              <div className="flex items-baseline">
                <p className="text-2xl font-semibold text-gray-900">
                  ${analytics.revenue.total.toLocaleString()}
                </p>
                <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                  analytics.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {analytics.revenue.growth >= 0 ? (
                    <TrendingUp className="w-4 h-4 mr-1" />
                  ) : (
                    <TrendingDown className="w-4 h-4 mr-1" />
                  )}
                  {Math.abs(analytics.revenue.growth)}%
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Calendar className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">Monthly Revenue</p>
              <p className="text-2xl font-semibold text-gray-900">
                ${analytics.revenue.monthly.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">Total Bookings</p>
              <p className="text-2xl font-semibold text-gray-900">
                {analytics.bookings.total}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Status Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Booking Status Distribution</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{analytics.bookings.confirmed}</div>
            <div className="text-sm text-gray-500">Confirmed</div>
            <div className="text-xs text-gray-400">
              {((analytics.bookings.confirmed / analytics.bookings.total) * 100).toFixed(1)}%
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600">{analytics.bookings.pending}</div>
            <div className="text-sm text-gray-500">Pending</div>
            <div className="text-xs text-gray-400">
              {((analytics.bookings.pending / analytics.bookings.total) * 100).toFixed(1)}%
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600">{analytics.bookings.cancelled}</div>
            <div className="text-sm text-gray-500">Cancelled</div>
            <div className="text-xs text-gray-400">
              {((analytics.bookings.cancelled / analytics.bookings.total) * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Top Destinations */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Top Destinations</h2>
        <div className="space-y-4">
          {analytics.topDestinations.map((destination, index) => (
            <div key={destination.location} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-sm font-medium text-purple-600">{index + 1}</span>
                </div>
                <div>
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{destination.location}</span>
                  </div>
                  <div className="text-xs text-gray-500">{destination.bookings} bookings</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">
                  ${destination.revenue.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">Revenue</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Product Performance */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Product Performance</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <Hotel className="w-5 h-5 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-gray-900">Hotels</span>
              </div>
              <span className="text-xs text-gray-500">{analytics.productPerformance.hotels.count} properties</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Bookings:</span>
                <span className="font-medium">{analytics.productPerformance.hotels.bookings}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Revenue:</span>
                <span className="font-medium">${analytics.productPerformance.hotels.revenue.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <MapPin className="w-5 h-5 text-green-600 mr-2" />
                <span className="text-sm font-medium text-gray-900">Activities</span>
              </div>
              <span className="text-xs text-gray-500">{analytics.productPerformance.activities.count} activities</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Bookings:</span>
                <span className="font-medium">{analytics.productPerformance.activities.bookings}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Revenue:</span>
                <span className="font-medium">${analytics.productPerformance.activities.revenue.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <Package className="w-5 h-5 text-purple-600 mr-2" />
                <span className="text-sm font-medium text-gray-900">Packages</span>
              </div>
              <span className="text-xs text-gray-500">{analytics.productPerformance.packages.count} packages</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Bookings:</span>
                <span className="font-medium">{analytics.productPerformance.packages.bookings}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Revenue:</span>
                <span className="font-medium">${analytics.productPerformance.packages.revenue.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Trends Chart Placeholder */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Booking Trends</h2>
        <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-center">
            <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Chart Coming Soon</h3>
            <p className="mt-1 text-sm text-gray-500">
              Interactive charts will be available with Chart.js integration
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Analytics